
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2, FileText, Upload, Link as LinkIcon, Database, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface UploadedFileData {
  name: string;
  url: string;
  size: number;
  type: string;
}

const KnowledgeBaseDocuments = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const kbData = location.state?.kbData || {};
  
  // Initialize with files from previous step if they exist
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileData[]>(
    kbData.uploadedFiles || []
  );
  const [connectedUrls, setConnectedUrls] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>("upload");
  const [newUrl, setNewUrl] = useState("");
  
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).map(file => ({
        name: file.name,
        url: '', // Will be uploaded when KB is created
        size: file.size,
        type: file.type
      }));
      setUploadedFiles([...uploadedFiles, ...newFiles]);
      toast({
        title: "Files added",
        description: `${newFiles.length} file(s) have been added to your knowledge base.`
      });
    }
  };
  
  const handleAddUrl = () => {
    if (newUrl) {
      setConnectedUrls([...connectedUrls, newUrl]);
      setNewUrl("");
      toast({
        title: "URL added",
        description: "The URL has been connected to your knowledge base."
      });
    }
  };
  
  const handleRemoveFile = (index: number) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
  };
  
  const handleRemoveUrl = (index: number) => {
    const newUrls = [...connectedUrls];
    newUrls.splice(index, 1);
    setConnectedUrls(newUrls);
  };
  
  const handleContinue = () => {
    // Combine the current KB data with the documents information
    const updatedKbData = {
      ...kbData,
      documents: {
        files: uploadedFiles,
        urls: connectedUrls
      }
    };
    
    navigate("/knowledge-base-confirmation", { state: { kbData: updatedKbData } });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-serif font-bold text-black mb-4">Add Documents</h1>
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
              <div className="flex items-center gap-1">
                <CheckCircle2 size={16} className="text-green-600" />
                <span>Basic Information</span>
              </div>
              <span>→</span>
              <div className="flex items-center gap-1">
                <CheckCircle2 size={16} className="text-green-600" />
                <span>Additional Details</span>
              </div>
              <span>→</span>
              <div className="flex items-center gap-1 font-medium">
                <FileText size={16} />
                <span>Documents</span>
              </div>
              <span>→</span>
              <div className="flex items-center gap-1 text-gray-400">
                <span>Review & Confirmation</span>
              </div>
            </div>
          </div>
          
          <Card className="border-2 border-black">
            <CardHeader className="bg-slate-100 border-b border-black">
              <CardTitle className="font-serif flex items-center">
                <FileText className="mr-2" size={24} />
                Add Content to: {kbData.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <Tabs defaultValue="upload" value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-3 mb-6">
                  <TabsTrigger value="upload">
                    <Upload size={16} className="mr-2" />
                    Upload Files
                  </TabsTrigger>
                  <TabsTrigger value="url">
                    <LinkIcon size={16} className="mr-2" />
                    Connect URLs
                  </TabsTrigger>
                  <TabsTrigger value="database">
                    <Database size={16} className="mr-2" />
                    Connect Database
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="upload" className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Drag and drop files here</h3>
                    <p className="text-gray-600 mb-4">Supports PDFs, Word documents, Excel, and text files</p>
                    <div className="mt-4">
                      <Label htmlFor="file-upload" className="cursor-pointer">
                        <span className="bg-black text-white px-4 py-2 rounded-md hover:bg-black/80">
                          Browse Files
                        </span>
                        <Input 
                          id="file-upload" 
                          type="file" 
                          multiple 
                          className="hidden" 
                          onChange={handleFileUpload} 
                        />
                      </Label>
                    </div>
                  </div>
                  
                  {uploadedFiles.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-medium mb-3">Uploaded Files</h3>
                      <div className="space-y-2">
                        {uploadedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                            <div className="flex items-center gap-2">
                              <FileText size={16} />
                              <span>{file.name}</span>
                              {file.url && <span className="text-xs text-green-600">(Uploaded)</span>}
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleRemoveFile(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="url" className="space-y-4">
                  <div className="flex gap-3">
                    <Input 
                      placeholder="https://example.com/resource" 
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                    />
                    <Button onClick={handleAddUrl}>Add URL</Button>
                  </div>
                  
                  {connectedUrls.length > 0 && (
                    <div className="mt-6">
                      <h3 className="font-medium mb-3">Connected URLs</h3>
                      <div className="space-y-2">
                        {connectedUrls.map((url, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                            <div className="flex items-center gap-2">
                              <LinkIcon size={16} />
                              <span className="text-blue-600 hover:underline">
                                {url}
                              </span>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleRemoveUrl(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="database">
                  <div className="text-center py-10">
                    <Database className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Connect to a Database</h3>
                    <p className="text-gray-600 mb-4">Coming soon - You'll be able to connect to SQL databases, APIs, and more.</p>
                    <Button variant="outline" disabled>Connect Database</Button>
                  </div>
                </TabsContent>
              </Tabs>
              
              <div className="flex justify-between mt-8 pt-4 border-t border-gray-200">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => navigate("/knowledge-base-review", { 
                    state: { 
                      aiSuggestions: location.state?.aiSuggestions,
                      uploadedFiles: location.state?.uploadedFiles,
                      sourceMethod: location.state?.sourceMethod 
                    } 
                  })}
                >
                  Back
                </Button>
                <Button 
                  onClick={handleContinue}
                  className="px-8"
                  disabled={uploadedFiles.length === 0 && connectedUrls.length === 0}
                >
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default KnowledgeBaseDocuments;
